import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import AppFooter from "@/components/AppFooter";
import CookieBanner from "@/components/CookieBanner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import CompactPackageCard from "@/components/CompactPackageCard";
import axios from "axios";

import { API, BACKEND_URL } from "@/lib/api";


export default function LandingPage({ onLogin }) {
  const { language, t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        // Ensure response data is an array before setting state
        if (Array.isArray(response.data)) {
          setPackages(response.data);
        } else {
          console.error("API /packages returned non-array data:", response.data);
          setPackages([]);
        }
      } catch (error) {
        console.error("Failed to fetch packages:", error);
        setPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    };

    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${API}/branding/logo`);
        if (response.data && response.data.logo_url) {
          setLogoUrl(`${BACKEND_URL}${response.data.logo_url}`);

        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };

    fetchPackages();
    fetchLogo();
  }, []);

  return (
    <div className="min-h-screen bg-brand-background overflow-hidden text-brand-text font-['Plus_Jakarta_Sans']">
      <div className="paper-texture opacity-[0.05]" />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo - dynamisch */}
          <a href="/" className="flex items-center">
            <img
              src={logoUrl || "/assets/moki-logo.svg"}
              alt="MOKI PLANER"
              className="h-12 md:h-14 lg:h-16"
              style={{ transform: 'scale(var(--logo-scale, 1))' }}
            />
          </a>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button
              onClick={onLogin}
              className="rounded-full px-8 py-5 bg-brand-text hover:bg-brand-accent text-white font-black uppercase text-[10px] tracking-widest transition-all shadow-xl"
            >
              {language === 'de' ? 'Anmelden' : 'Sign In'}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Based on Entwurf */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background with diagonal split */}
        <div className="absolute inset-0 z-0">
          {/* Left side - Brand Background */}
          <div className="absolute left-0 top-0 w-full h-full bg-brand-background" />
          {/* Right side - Subtle diagonal tint */}
          <div
            className="absolute right-0 top-0 h-full bg-brand-accent/5"
            style={{
              width: '60%',
              clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)'
            }}
          />
        </div>

        {/* Scattered Event Images - Left Side (unique images) */}
        <div className="absolute left-0 top-0 w-1/2 h-full hidden lg:block pointer-events-none z-10">
          <motion.div
            animate={{ y: [0, -8, 0], rotate: [-8, -6, -8] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-8 w-40 h-40 rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80"
              alt="Birthday"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0], rotate: [6, 8, 6] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute top-48 left-44 w-32 h-32 rounded-[1.5rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=80"
              alt="Wedding"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, -12, 0], x: [0, 5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-1/3 left-4 w-44 h-44 rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1464349153735-7db50ed83c84?w=400&q=80"
              alt="Christmas Decoration"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, 8, 0], rotate: [-5, -3, -5] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
            className="absolute bottom-40 left-16 w-36 h-36 rounded-[1.5rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=400&q=80"
              alt="Party Glasses"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, -6, 0], scale: [1, 1.02, 1] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-24 left-48 w-28 h-28 rounded-[1.2rem] overflow-hidden shadow-xl border-4 border-white/50"
          >
            <img
              src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=400&q=80"
              alt="Friends"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>

        {/* Scattered Event Images - Right Side (unique images) */}
        <div className="absolute right-0 top-0 w-1/3 h-1/2 hidden lg:block pointer-events-none z-10">
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [8, 10, 8] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            className="absolute top-24 right-8 w-36 h-36 rounded-[1.5rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1478146896981-b80fe463b330?w=400&q=80"
              alt="Baby Shower"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, 12, 0], rotate: [-6, -4, -6] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="absolute top-48 right-44 w-32 h-32 rounded-[1.5rem] overflow-hidden shadow-2xl border-4 border-white/60"
          >
            <img
              src="https://images.unsplash.com/photo-1627556704302-624286467c65?w=400&q=80"
              alt="Graduation"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            className="absolute top-16 right-1/3 w-28 h-28 rounded-[1.2rem] overflow-hidden shadow-xl border-4 border-white/50"
          >
            <img
              src="https://images.unsplash.com/photo-1482575832494-771f74bf6857?w=400&q=80"
              alt="Corporate Event"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-6 relative z-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left lg:pl-8 xl:pl-16">
              {/* Logo Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-white/50 backdrop-blur-md rounded-full px-5 py-2 shadow-sm border border-brand-accent/20 mb-8"
              >
                <span className="text-brand-accent text-lg">✨</span>
                <span className="text-brand-accent font-black uppercase text-[10px] tracking-[0.3em]">mokiplaner.com</span>
              </motion.div>

              {/* Main Headline - Gold/Ochre Color */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] tracking-tighter mb-8 text-brand-text drop-shadow-sm"
              >
                {language === 'de' ? (
                  <>
                    Dein Event<br />
                    beginnt hier
                  </>
                ) : (
                  <>
                    Your Event<br />
                    Starts Here
                  </>
                )}
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl sm:text-2xl lg:text-3xl font-serif italic mb-10 text-brand-accent opacity-80"
              >
                {language === 'de'
                  ? 'einladen · planen · erleben'
                  : 'invite · plan · experience'}
              </motion.p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Button
                  onClick={onLogin}
                  size="lg"
                  className="rounded-full px-12 py-8 bg-brand-text hover:bg-brand-accent text-white shadow-2xl transition-all font-black uppercase text-xs tracking-widest flex items-center gap-3 active:scale-95"
                >
                  {language === 'de' ? 'Jetzt starten' : 'Get Started'}
                  <span className="text-xl">→</span>
                </Button>
              </motion.div>
            </div>

            {/* Right - Smartphone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative flex justify-center lg:justify-end"
            >
              <img
                src="/assets/smartphones-mockup.png"
                alt="MOKI PLANER App"
                className="w-full max-w-lg xl:max-w-xl 2xl:max-w-2xl drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>

        {/* Mobile - Floating images (same as desktop, no concerts) */}
        <div className="absolute inset-0 lg:hidden pointer-events-none z-5">
          {/* Top right - Birthday balloons */}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-24 right-4 w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 border-white/50 opacity-70"
          >
            <img
              src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=200&q=60"
              alt="Geburtstag"
              className="w-full h-full object-cover"
            />
          </motion.div>
          {/* Top left - Wedding */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute top-28 left-4 w-16 h-16 rounded-xl overflow-hidden shadow-lg border-2 border-white/50 opacity-60"
          >
            <img
              src="https://images.unsplash.com/photo-1519741497674-611481863552?w=200&q=60"
              alt="Hochzeit"
              className="w-full h-full object-cover"
            />
          </motion.div>
          {/* Middle left - Party */}
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-52 left-2 w-14 h-14 rounded-xl overflow-hidden shadow-lg border-2 border-white/50 opacity-50"
          >
            <img
              src="https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=200&q=60"
              alt="Party"
              className="w-full h-full object-cover"
            />
          </motion.div>
          {/* Bottom right - Friends */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            className="absolute bottom-36 right-4 w-16 h-16 rounded-xl overflow-hidden shadow-lg border-2 border-white/50 opacity-60"
          >
            <img
              src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=200&q=60"
              alt="Freunde"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-accent/5 rounded-full blur-[120px] -mr-64 -mt-64" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-text mb-6">
              {language === 'de' ? 'Alles für dein perfektes Event' : 'Everything for Your Perfect Event'}
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto font-medium">
              {language === 'de'
                ? 'Von der Einladung bis zur Nachbereitung – alles in einer App'
                : 'From invitation to follow-up – everything in one app'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                emoji: "📨",
                title: language === 'de' ? "einladen" : "invite",
                desc: language === 'de'
                  ? "Aus einer digitalen Einladung ein gemeinsames Erlebnis planen. Erstelle dein Event in wenigen Minuten und teile einen einzigen Link mit deinen Gästen."
                  : "Turn a digital invitation into a shared experience. Create your event in minutes and share a single link with your guests."
              },
              {
                emoji: "📋",
                title: language === 'de' ? "planen" : "plan",
                desc: language === 'de'
                  ? "Deine Gäste sagen zu oder ab, dürfen sich aktiv einbringen, übernehmen Aufgaben, stimmen ab, posten Musikwünsche oder Erinnerungen."
                  : "Your guests RSVP, actively participate, take on tasks, vote, post music requests or memories."
              },
              {
                emoji: "🎉",
                title: language === 'de' ? "erleben" : "experience",
                desc: language === 'de'
                  ? "Dein Event wächst Schritt für Schritt und du behältst entspannt den Überblick."
                  : "Your event grows step by step and you stay relaxed with a clear overview."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-brand-background rounded-[3rem] p-10 text-center border border-gray-100 hover:shadow-2xl hover:border-brand-accent/20 transition-all duration-500 group"
              >
                <div className="text-6xl mb-6 group-hover:scale-110 transition-transform">{feature.emoji}</div>
                <h3 className="font-serif text-3xl text-brand-text mb-4">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-medium">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types Section */}
      <section className="py-20 px-6 bg-brand-background">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-text mb-4">
              {language === 'de' ? 'Für jeden Anlass' : 'For Every Occasion'}
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-5">
            {[
              { emoji: "🎂", label: language === 'de' ? "Geburtstag" : "Birthday" },
              { emoji: "💒", label: language === 'de' ? "Hochzeit" : "Wedding" },
              { emoji: "🎉", label: "Party" },
              { emoji: "👰", label: language === 'de' ? "Junggesellenabschied" : "Bachelorette" },
              { emoji: "🎓", label: language === 'de' ? "Abschluss" : "Graduation" },
              { emoji: "💍", label: language === 'de' ? "Jubiläum" : "Anniversary" },
              { emoji: "👶", label: language === 'de' ? "Babyparty" : "Baby Shower" },
              { emoji: "🏢", label: language === 'de' ? "Firmenfeier" : "Corporate" },
              { emoji: "🎄", label: language === 'de' ? "Weihnachten" : "Christmas" },
              { emoji: "🎆", label: language === 'de' ? "Silvester" : "New Year" },
              { emoji: "🐣", label: language === 'de' ? "Ostern" : "Easter" },
              { emoji: "❤️", label: language === 'de' ? "Valentinstag" : "Valentine" },
            ].map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white border border-gray-100 rounded-full px-7 py-4 shadow-sm hover:shadow-xl hover:border-brand-accent/20 transition-all cursor-default"
              >
                <span className="text-3xl">{type.emoji}</span>
                <span className="font-black uppercase text-[10px] tracking-widest text-brand-text opacity-70">{type.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2a6a68] mb-4">
              {language === 'de' ? 'Unsere Pakete' : 'Our Packages'}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {language === 'de'
                ? 'Wähle das passende Paket für dein Event'
                : 'Choose the right package for your event'}
            </p>
          </motion.div>

          {loadingPackages ? (
            <div className="flex justify-center">
              <div className="w-10 h-10 border-4 border-[#2a6a68] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className={`grid grid-cols-1 ${packages.length === 4 ? 'md:grid-cols-4' : packages.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 items-stretch`}>
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="h-full"
                >
                  <CompactPackageCard
                    pkg={pkg}
                    isRecommended={pkg.is_recommended === true}
                    onSelect={onLogin}
                    buttonText={language === 'de' ? 'Jetzt starten' : 'Get Started'}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-brand-text text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-brand-accent/20 rounded-full blur-[100px] -ml-40 -mt-40" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-5xl sm:text-6xl mb-8 leading-tight tracking-tighter">
              {language === 'de' ? 'Bereit für dein nächstes Event?' : 'Ready for Your Next Event?'}
            </h2>
            <p className="text-lg opacity-70 mb-12 max-w-2xl mx-auto font-medium">
              {language === 'de'
                ? 'Starte jetzt kostenlos und erlebe, wie einfach Eventplanung sein kann.'
                : 'Start free now and experience how easy event planning can be.'}
            </p>
            <Button
              onClick={onLogin}
              size="lg"
              className="rounded-full px-12 py-8 bg-brand-accent text-white hover:bg-white hover:text-brand-text shadow-2xl transition-all font-bold uppercase tracking-widest text-xs"
            >
              {language === 'de' ? 'Kostenlos starten' : 'Start for Free'}
              <span className="ml-3 text-xl">→</span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>
  );
}
